import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '../../contexts/AuthContext'
import { Surface, Button, Chip, Skeleton } from '@heroui/react'
import DollarSign from '~icons/solar/dollar-minimalistic-bold'
import TrendingUp from '~icons/solar/graph-up-bold'
import CalendarIcon from '~icons/solar/calendar-bold'
import Download from '~icons/solar/download-bold'
import CardIcon from '~icons/solar/card-2-bold'
import Plus from '~icons/solar/add-circle-bold'
import AlertCircle from '~icons/solar/danger-circle-bold'
import CheckCircle from '~icons/solar/check-circle-bold'
import Clock from '~icons/solar/clock-circle-bold'
import Trash2 from '~icons/solar/trash-bin-trash-bold'
import Edit2 from '~icons/solar/pen-bold'
import Star from '~icons/solar/star-bold'
import AddPaymentMethodModal, { PaymentMethodData } from '../../components/modals/AddPaymentMethodModal'
import { supabase } from '../../lib/supabase'
import { queryKeys } from '../../lib/queryClient'
import AffiliateHeader from '../../components/affiliate/AffiliateHeader'
import { DataTable } from '../../components/DataTable'
import { DialogDrawer } from '../../components/DialogDrawer'
import { formatDate, formatDateTime } from '../../lib/format'
import { getPaymentMethodIcon, getPaymentMethodIconColor } from '../../lib/paymentMethodIcons'

interface PaymentStats {
  availableBalance: number
  pendingBalance: number
  lifetimeEarnings: number
  nextPaymentDate: string
}

interface PaymentHistoryItem {
  id: string
  amount: number
  status: 'pending' | 'processing' | 'completed' | 'failed'
  paymentDate: string
  processedAt?: string
  method: string
  proofImageUrl?: string
  invoiceUrl?: string
}

interface PaymentMethod {
  id: string
  type: 'paypal' | 'stripe' | 'wise' | 'crypto'
  name: string
  details: string
  isDefault: boolean
}

interface PaymentsData {
  stats: PaymentStats
  methods: PaymentMethod[]
  history: PaymentHistoryItem[]
}

function nextPaymentDate(): string {
  const now = new Date()
  return new Date(now.getFullYear(), now.getMonth() + 1, 15).toISOString().split('T')[0]
}

const EMPTY_PAYMENTS_DATA: PaymentsData = {
  stats: { availableBalance: 0, pendingBalance: 0, lifetimeEarnings: 0, nextPaymentDate: nextPaymentDate() },
  methods: [],
  history: [],
}

const MINIMUM_PAYOUT = 50

function ProfessionalPayments() {
  const { user, logout } = useAuth()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [showAddMethodModal, setShowAddMethodModal] = useState(false)
  const [selectedPayment, setSelectedPayment] = useState<PaymentHistoryItem | null>(null)
  const [editingMethod, setEditingMethod] = useState<PaymentMethod | null>(null)
  const [historyPage, setHistoryPage] = useState(1)

  const paymentsQuery = useQuery({
    queryKey: queryKeys.payments(user?.affiliateId ?? ''),
    enabled: !!user?.affiliateId,
    queryFn: async (): Promise<PaymentsData> => {
      const { data, error } = await supabase.rpc('get_affiliate_payments', {
        p_affiliate_id: user?.affiliateId,
      })
      if (error) throw error

      const methods: PaymentMethod[] = (data.methods ?? []).map((m: any) => ({
        id: m.id,
        type: m.type,
        name: m.name,
        details: typeof m.details === 'string' ? m.details : JSON.stringify(m.details),
        isDefault: m.is_default,
      }))

      const history: PaymentHistoryItem[] = (data.history ?? []).map((h: any) => ({
        id: h.id,
        amount: parseFloat(h.amount) || 0,
        status: h.status,
        paymentDate: h.requested_at,
        processedAt: h.processed_at,
        method: h.payout_method,
        proofImageUrl: h.proof_image_url,
        invoiceUrl: h.invoice_url,
      }))

      return {
        stats: { ...data.stats, nextPaymentDate: nextPaymentDate() },
        methods,
        history,
      }
    },
  })

  const paymentsData = user?.affiliateId
    ? paymentsQuery.data ?? EMPTY_PAYMENTS_DATA
    : EMPTY_PAYMENTS_DATA
  const loading = !!user?.affiliateId && paymentsQuery.isLoading
  const { stats, methods: paymentMethods, history: paymentHistory } = paymentsData

  const HISTORY_PAGE_SIZE = 10
  const historyPageCount = Math.max(1, Math.ceil(paymentHistory.length / HISTORY_PAGE_SIZE))
  const paginatedHistory = paymentHistory.slice(
    (historyPage - 1) * HISTORY_PAGE_SIZE,
    historyPage * HISTORY_PAGE_SIZE
  )

  useEffect(() => {
    setHistoryPage(1)
  }, [paymentHistory.length])

  const patchMethods = (updater: (methods: PaymentMethod[]) => PaymentMethod[]) =>
    queryClient.setQueryData<PaymentsData>(
      queryKeys.payments(user?.affiliateId ?? ''),
      (prev) => prev && { ...prev, methods: updater(prev.methods) }
    )

  const handleAddPaymentMethod = async (methodData: PaymentMethodData) => {
    if (!user?.affiliateId) return

    try {
      if (editingMethod) {
        // If setting as default, unset others BEFORE updating (to avoid unique constraint violation)
        if (methodData.isDefault && !editingMethod.isDefault) {
          await supabase
            .from('payment_methods')
            .update({ is_default: false })
            .eq('affiliate_id', user.affiliateId)
            .neq('id', editingMethod.id)
        }

        // Update existing method in database
        // For crypto, store details as object with full address; for others, store as string or object
        let detailsToStore: any = methodData.details
        if (methodData.type === 'crypto' && methodData.cryptoAddress) {
          detailsToStore = {
            cryptoAddress: methodData.cryptoAddress,
            cryptoNetwork: methodData.cryptoNetwork
          }
        }

        const { error } = await supabase
          .from('payment_methods')
          .update({
            type: methodData.type,
            name: methodData.name,
            details: detailsToStore,
            is_default: methodData.isDefault
          })
          .eq('id', editingMethod.id)
          .eq('affiliate_id', user.affiliateId)

        if (error) {
          console.error('Error updating payment method:', error)
          alert('Failed to update payment method. Please try again.')
          return
        }

        // Update local state
        const updatedMethod: PaymentMethod = {
          ...editingMethod,
          type: methodData.type,
          name: methodData.name,
          details: methodData.details,
          isDefault: methodData.isDefault
        }

        patchMethods(prev => {
          let methods = prev.map(m =>
            m.id === editingMethod.id ? updatedMethod : m
          )

          if (methodData.isDefault) {
            methods = methods.map(m =>
              m.id === editingMethod.id ? m : { ...m, isDefault: false }
            )
          }

          return methods
        })

        setEditingMethod(null)
      } else {
        // If setting as default, unset others BEFORE inserting (to avoid unique constraint violation)
        if (methodData.isDefault) {
          await supabase
            .from('payment_methods')
            .update({ is_default: false })
            .eq('affiliate_id', user.affiliateId)
        }

        // Add new method to database
        // For crypto, store details as object with full address; for others, store as string or object
        let detailsToStore: any = methodData.details
        if (methodData.type === 'crypto' && methodData.cryptoAddress) {
          detailsToStore = {
            cryptoAddress: methodData.cryptoAddress,
            cryptoNetwork: methodData.cryptoNetwork
          }
        }

        const { data: insertedMethod, error } = await supabase
          .from('payment_methods')
          .insert({
            affiliate_id: user.affiliateId,
            organization_id: user.organizationId,
            type: methodData.type,
            name: methodData.name,
            details: detailsToStore,
            is_default: methodData.isDefault
          })
          .select()
          .single()

        if (error) {
          console.error('Error adding payment method:', error)
          alert('Failed to add payment method. Please try again.')
          return
        }

        // Add to local state
        const newMethod: PaymentMethod = {
          id: insertedMethod.id,
          type: methodData.type,
          name: methodData.name,
          details: methodData.details,
          isDefault: methodData.isDefault
        }

        patchMethods(prev => {
          let methods = [...prev, newMethod]

          if (methodData.isDefault) {
            methods = methods.map(m =>
              m.id === newMethod.id ? m : { ...m, isDefault: false }
            )
          }

          return methods
        })
      }
    } catch (error) {
      console.error('Error handling payment method:', error)
    }
  }

  const handleDeletePaymentMethod = async (methodId: string) => {
    if (!user?.affiliateId) return

    // Check if trying to delete the default method
    const method = paymentMethods.find(m => m.id === methodId)
    if (method?.isDefault) {
      alert('Cannot delete the default payment method. Please set another method as default first.')
      return
    }

    if (window.confirm('Are you sure you want to delete this payment method?')) {
      const { error } = await supabase
        .from('payment_methods')
        .delete()
        .eq('id', methodId)
        .eq('affiliate_id', user.affiliateId)

      if (error) {
        console.error('Error deleting payment method:', error)
        alert('Failed to delete payment method. Please try again.')
        return
      }

      patchMethods(prev => prev.filter(m => m.id !== methodId))
    }
  }

  const handleSetDefaultMethod = async (methodId: string) => {
    if (!user?.affiliateId) return

    // First, unset all as default
    await supabase
      .from('payment_methods')
      .update({ is_default: false })
      .eq('affiliate_id', user.affiliateId)

    // Then set the selected one as default
    const { error } = await supabase
      .from('payment_methods')
      .update({ is_default: true })
      .eq('id', methodId)
      .eq('affiliate_id', user.affiliateId)

    if (error) {
      console.error('Error setting default payment method:', error)
      alert('Failed to set default payment method. Please try again.')
      return
    }

    patchMethods(prev =>
      prev.map(m => ({ ...m, isDefault: m.id === methodId }))
    )
  }

  const handleEditMethod = (method: PaymentMethod) => {
    setEditingMethod(method)
    setShowAddMethodModal(true)
  }

  const handleCloseModal = () => {
    setShowAddMethodModal(false)
    setEditingMethod(null)
  }


  const getStatusIcon = (status: PaymentHistoryItem['status']) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="h-4 w-4" />
      case 'processing':
        return <Clock className="h-4 w-4" />
      case 'pending':
        return <AlertCircle className="h-4 w-4" />
      case 'failed':
        return <AlertCircle className="h-4 w-4" />
    }
  }

  const getStatusColor = (status: PaymentHistoryItem['status']): 'success' | 'accent' | 'warning' | 'danger' => {
    switch (status) {
      case 'completed':
        return 'success'
      case 'processing':
        return 'accent'
      case 'pending':
        return 'warning'
      case 'failed':
        return 'danger'
    }
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <AffiliateHeader notifications={[]} />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-4 sm:py-8">
        {/* Balance Overview */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {/* Available Balance */}
          <Surface variant="default" className="rounded-xl p-6">
            <div className="flex items-start justify-between mb-4">
              <div className="p-2.5 bg-surface-secondary rounded-xl">
                <DollarSign className="h-5 w-5 text-success" />
              </div>
            </div>
            <p className="text-sm text-muted mb-1">Available Balance</p>
            {loading ? (
              <Skeleton className="h-9 w-28 rounded" />
            ) : (
              <p className="text-3xl font-bold text-foreground">${stats.availableBalance.toLocaleString('en-US', { minimumFractionDigits: 2 })}</p>
            )}
            <p className="text-xs text-muted mt-2">Ready to withdraw</p>
          </Surface>

          {/* Pending Balance */}
          <Surface variant="default" className="rounded-xl p-6">
            <div className="flex items-start justify-between mb-4">
              <div className="p-2.5 bg-surface-secondary rounded-xl">
                <Clock className="h-5 w-5 text-warning" />
              </div>
            </div>
            <p className="text-sm text-muted mb-1">Pending Balance</p>
            {loading ? (
              <Skeleton className="h-9 w-28 rounded" />
            ) : (
              <p className="text-3xl font-bold text-foreground">${stats.pendingBalance.toLocaleString('en-US', { minimumFractionDigits: 2 })}</p>
            )}
            <p className="text-xs text-muted mt-2">Processing period</p>
          </Surface>

          {/* Lifetime Earnings */}
          <Surface variant="default" className="rounded-xl p-6">
            <div className="flex items-start justify-between mb-4">
              <div className="p-2.5 bg-surface-secondary rounded-xl">
                <TrendingUp className="h-5 w-5 text-accent" />
              </div>
            </div>
            <p className="text-sm text-muted mb-1">Lifetime Earnings</p>
            {loading ? (
              <Skeleton className="h-9 w-28 rounded" />
            ) : (
              <p className="text-3xl font-bold text-foreground">${stats.lifetimeEarnings.toLocaleString('en-US', { minimumFractionDigits: 2 })}</p>
            )}
            <p className="text-xs text-muted mt-2">Total commissions earned</p>
          </Surface>
        </div>

        {/* Payment Information Section */}
        <Surface variant="secondary" className="rounded-xl p-6 mb-8">
          <div className="flex items-start gap-4">
            <div className="p-3 bg-accent rounded-lg shrink-0">
              <AlertCircle className="h-6 w-6 text-accent-foreground" />
            </div>
            <div className="flex-1">
              <h2 className="text-lg font-semibold text-foreground mb-2">Monthly Payment Schedule</h2>
              <div className="space-y-2 text-sm text-foreground">
                <p className="flex items-center gap-2">
                  <CalendarIcon className="h-4 w-4 text-accent" />
                  Payments are processed monthly by our admin team
                </p>
                <p className="flex items-center gap-2">
                  <DollarSign className="h-4 w-4 text-accent" />
                  Minimum balance required: <span className="font-semibold">${MINIMUM_PAYOUT}</span>
                </p>
                <p className="flex items-center gap-2">
                  <CardIcon className="h-4 w-4 text-accent" />
                  Payments sent to your default payment method
                </p>
                <p className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-accent" />
                  You'll receive proof of payment and invoice after each transaction
                </p>
              </div>
            </div>
          </div>
        </Surface>

        {/* Payment Methods */}
        <Surface variant="default" className="rounded-xl p-6 mb-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-semibold text-foreground">Payment Methods</h2>
            <Button variant="outline" onPress={() => setShowAddMethodModal(true)}>
              <Plus className="h-4 w-4" />
              Add Method
            </Button>
          </div>

          <div className="space-y-3">
            {loading ? (
              [1, 2].map(i => <Skeleton key={i} className="h-[4.5rem] rounded-lg" />)
            ) : paymentMethods.map(method => {
              const MethodLogo = getPaymentMethodIcon(method.type)
              return (
              <div
                key={method.id}
                className="flex items-center justify-between p-4 border border-black/10 dark:border-white/10 rounded-lg hover:border-accent/40 transition-colors"
              >
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-surface-secondary rounded-lg">
                    <MethodLogo
                      className={method.type === 'stripe' ? 'h-6 w-6' : 'h-5 w-5'}
                      style={getPaymentMethodIconColor(method.type)}
                    />
                  </div>
                  <div>
                    <p className="font-medium text-foreground">{method.name}</p>
                    <p className="text-sm text-muted">{method.details}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {method.isDefault ? (
                    <Chip color="accent" size="sm">
                      <Star className="h-3 w-3" />
                      Default
                    </Chip>
                  ) : (
                    <Button variant="outline" size="sm" onPress={() => handleSetDefaultMethod(method.id)}>
                      Set Default
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    isIconOnly
                    onPress={() => handleEditMethod(method)}
                    aria-label="Edit payment method"
                  >
                    <Edit2 className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    isIconOnly
                    onPress={() => handleDeletePaymentMethod(method.id)}
                    isDisabled={method.isDefault}
                    aria-label="Delete payment method"
                    className="hover:text-danger hover:bg-danger-soft"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              )
            })}
          </div>
        </Surface>

        {/* Payment History */}
        <Surface variant="default" className="rounded-2xl overflow-hidden">
          <div className="p-6 border-b border-black/10 dark:border-white/10">
            <h2 className="text-lg font-semibold text-foreground">Payment History</h2>
            <p className="text-sm text-muted mt-1">View all payments processed by admin</p>
          </div>

          {loading ? (
            <div className="p-6 space-y-3">
              {[1, 2, 3].map(i => <Skeleton key={i} className="h-10 rounded-lg" />)}
            </div>
          ) : paymentHistory.length === 0 ? (
            <div className="text-center py-12">
              <Download className="h-10 w-10 text-muted mx-auto mb-3" />
              <p className="text-muted">No payment history yet</p>
            </div>
          ) : (
            <DataTable
              ariaLabel="Payment history"
              className="min-w-full"
              columns={[
                { key: 'date', header: 'Date', isRowHeader: true },
                { key: 'amount', header: 'Amount' },
                { key: 'method', header: 'Method' },
                { key: 'status', header: 'Status' },
                { key: 'proof', header: 'Proof & Invoice' },
              ]}
              rows={paginatedHistory}
              getRowId={(item) => item.id}
              pagination={{
                page: historyPage,
                pageCount: historyPageCount,
                total: paymentHistory.length,
                pageSize: HISTORY_PAGE_SIZE,
                onPageChange: setHistoryPage,
                itemLabel: 'payments',
              }}
              renderRow={(item) => [
                <span className="text-sm text-foreground">{formatDateTime(item.paymentDate)}</span>,
                <span className="text-sm font-medium text-foreground">
                  ${item.amount.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                </span>,
                <span className="flex items-center gap-1.5 text-sm text-muted capitalize">
                  {React.createElement(getPaymentMethodIcon(item.method), {
                    className: 'h-4 w-4 shrink-0',
                    style: getPaymentMethodIconColor(item.method),
                  })}
                  {item.method}
                </span>,
                <Chip color={getStatusColor(item.status)} size="sm">
                  {getStatusIcon(item.status)}
                  {item.status.charAt(0).toUpperCase() + item.status.slice(1)}
                </Chip>,
                item.status === 'completed' && item.proofImageUrl ? (
                  <button
                    onClick={() => setSelectedPayment(item)}
                    className="text-sm font-medium text-accent hover:underline"
                  >
                    View Details
                  </button>
                ) : (
                  <span className="text-sm text-muted">-</span>
                ),
              ]}
            />
          )}
        </Surface>
      </main>

      {/* Add/Edit Payment Method Modal */}
      <AddPaymentMethodModal
        isOpen={showAddMethodModal}
        onClose={handleCloseModal}
        onAdd={handleAddPaymentMethod}
        editingMethod={editingMethod}
      />

      {/* Payment Details Modal */}
      <DialogDrawer.Backdrop isOpen={!!selectedPayment} onOpenChange={(open) => !open && setSelectedPayment(null)}>
        <DialogDrawer.Dialog className="sm:max-w-lg">
            <DialogDrawer.CloseTrigger />
            <DialogDrawer.Header>
              <DialogDrawer.Icon className="bg-accent-soft text-accent-soft-foreground">
                <Download className="size-5" />
              </DialogDrawer.Icon>
              <DialogDrawer.Heading>Payment Details</DialogDrawer.Heading>
            </DialogDrawer.Header>

            {selectedPayment && (
              <DialogDrawer.Body>
                <Surface variant="secondary" className="rounded-lg p-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-muted">Payment Date</p>
                      <p className="text-base font-medium text-foreground">{formatDate(selectedPayment.paymentDate)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted">Amount</p>
                      <p className="text-base font-medium text-foreground">${selectedPayment.amount.toLocaleString('en-US', { minimumFractionDigits: 2 })}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted">Payment Method</p>
                      <p className="text-base font-medium text-foreground">{selectedPayment.method}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted">Status</p>
                      <Chip color={getStatusColor(selectedPayment.status)} size="sm">
                        {getStatusIcon(selectedPayment.status)}
                        {selectedPayment.status.charAt(0).toUpperCase() + selectedPayment.status.slice(1)}
                      </Chip>
                    </div>
                  </div>
                </Surface>

                {/* Payment Proof */}
                {selectedPayment.proofImageUrl && (
                  <div className="mt-6">
                    <h3 className="text-base font-semibold text-foreground mb-3">Payment Proof</h3>
                    <div className="border border-black/10 dark:border-white/10 rounded-lg overflow-hidden bg-surface-secondary flex items-center justify-center p-4">
                      <img
                        src={selectedPayment.proofImageUrl}
                        alt="Payment Proof"
                        className="max-w-full h-auto rounded"
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                          e.currentTarget.parentElement!.innerHTML = '<p class="text-muted">Proof screenshot not available</p>';
                        }}
                      />
                    </div>
                    <p className="text-xs text-muted mt-2">Screenshot uploaded by admin</p>
                  </div>
                )}

                {/* Invoice Download */}
                {selectedPayment.invoiceUrl && (
                  <div className="mt-6">
                    <h3 className="text-base font-semibold text-foreground mb-3">Invoice</h3>
                    <a
                      href={selectedPayment.invoiceUrl}
                      download
                      className="flex items-center gap-3 p-4 border border-black/10 dark:border-white/10 rounded-lg hover:bg-surface-secondary transition-colors"
                    >
                      <Download className="h-8 w-8 text-accent" />
                      <div>
                        <p className="font-medium text-foreground">Download Invoice</p>
                        <p className="text-sm text-muted">PDF Document</p>
                      </div>
                    </a>
                  </div>
                )}
              </DialogDrawer.Body>
            )}

            <DialogDrawer.Footer>
              <Button className="w-full" onPress={() => setSelectedPayment(null)}>
                Close
              </Button>
            </DialogDrawer.Footer>
          </DialogDrawer.Dialog>
      </DialogDrawer.Backdrop>
    </div>
  )
}

export default ProfessionalPayments
